import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TRADES, COUNTIES, DURATIONS } from "@/constants/data";
import CountiesPicker from "@/components/CountiesPicker";

export default function PostJob() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    title: "",
    trade: TRADES[0],
    county: COUNTIES[0],
    counties: [],
    town: "",
    duration: DURATIONS[0],
    start_date: "",
    day_rate: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        ...f,
        day_rate: f.day_rate ? parseFloat(f.day_rate) : null,
        start_date: f.start_date || null,
        town: f.town || null,
      };
      const { data } = await api.post("/jobs", payload);
      toast.success("Job posted");
      navigate(`/jobs/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Post a Job</div>
      <h1 className="display text-4xl md:text-5xl mt-2">Post a Shift</h1>
      <p className="text-neutral-400 mt-2">Two minutes. No fees. Direct hire.</p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="post-job-form">
        <Field label="Job Title" required>
          <input data-testid="job-title-input" required value={f.title} onChange={set("title")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="e.g. Steel fixer needed for weekend shift" />
        </Field>
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Trade" required>
            <select data-testid="job-trade-select" value={f.trade} onChange={set("trade")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {TRADES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Duration" required>
            <select data-testid="job-duration-select" value={f.duration} onChange={set("duration")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {DURATIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="County" required>
            <select data-testid="job-county-select" value={f.county} onChange={set("county")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {COUNTIES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Town / Site (optional)">
            <input data-testid="job-town-input" value={f.town} onChange={set("town")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="e.g. Dundalk" />
          </Field>
          <Field label="Start Date">
            <input data-testid="job-start-input" type="date" value={f.start_date} onChange={set("start_date")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3 text-neutral-200" />
          </Field>
          <Field label="Day Rate (€)">
            <input data-testid="job-rate-input" type="number" min="0" step="10" value={f.day_rate} onChange={set("day_rate")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="250" />
          </Field>
        </div>
        <Field label="Also covers (optional)">
          <div className="text-[11px] text-neutral-500 -mt-1 mb-2">
            Tick every county this job covers. Workers searching those counties will see it too.
          </div>
          <CountiesPicker
            primary={f.county}
            value={f.counties}
            onChange={(next) => setF((s) => ({ ...s, counties: next }))}
          />
          {f.counties.length > 0 && (
            <div className="mt-2 text-[11px] uppercase tracking-widest text-amber-500">
              {f.counties.length} extra {f.counties.length === 1 ? "county" : "counties"} selected · more workers will see this
            </div>
          )}
        </Field>
        <Field label="Description" required>
          <textarea data-testid="job-description-input" required rows={6} value={f.description} onChange={set("description")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="Describe the work, tools required, PPE, hours…" />
        </Field>

        {error && <div data-testid="post-job-error" className="border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">{error}</div>}

        <button data-testid="post-job-submit" disabled={loading} className="w-full md:w-auto px-8 py-3 bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-60">
          {loading ? "Posting…" : "Post Job"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">
        {label}{required && <span className="text-amber-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
